/**
 * Query options factories for the family record feature. Same shape as
 * `patientQueries`: each section of the page reads its own slice via these
 * factories so a single endpoint failure can never break the whole page —
 * only the section that owns the failed query renders an inline error.
 */

import { queryOptions } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapFamily, mapFamilyMembers } from '@/features/families/mappers';
import { mapFamilyDocuments } from '@/features/family-record/mappers/familyDocument';
import { queryKeys } from '@/query/keys';

const DEFAULT_STALE_TIME = 60_000;

export const familyQueries = {
  /** Family record itself — name, custom_label, physical_chart, notes, timestamps. */
  detail: (guid: string) =>
    queryOptions({
      queryKey: queryKeys.family(guid),
      queryFn: ({ signal }) =>
        apiClient.getFamily({ guid }, { signal }).then((dto) => mapFamily(dto)),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  /**
   * Members list — kept separate so a 4xx/5xx on this endpoint only
   * degrades the Members card (and the proband / count read-outs that
   * derive from it) instead of taking the whole record offline.
   */
  members: (guid: string) =>
    queryOptions({
      queryKey: [...queryKeys.family(guid), 'members'] as const,
      queryFn: ({ signal }) =>
        apiClient
          .getFamilyMembers({ guid }, { signal })
          .then((page) => mapFamilyMembers(page.content, guid)),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),

  documents: (guid: string) =>
    queryOptions({
      queryKey: [...queryKeys.family(guid), 'documents'] as const,
      queryFn: ({ signal }) =>
        apiClient
          .listFamilyAttachments({ guid }, { signal })
          .then((page) => mapFamilyDocuments(page.content)),
      enabled: guid.length > 0,
      staleTime: DEFAULT_STALE_TIME,
    }),
};

export const familyRecordKeys = {
  all: queryKeys.family,
};
